import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, X } from 'lucide-react';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import { copyText } from '../../shared/lib/clipboard';
import { shortDateTime } from '../../shared/lib/datetime';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';
import { PublicProfileSheet, profileSeed } from '../profile/PublicProfileSheet';
import { fetchMilharRoundDetail } from './api';
import { formatPoolSharePct, poolSharePct } from '../../shared/milhar/poolShare';
import type { MilharTierId } from '../../shared/milhar/tiers';

/**
 * 某一期的完整账单。视觉规格 §12B.1 写了但一直没实现；2026-08-19 评审又提了一次，
 * 并要求详情里带**全部参与用户的投注**。
 *
 * <p><b>哈希在这里，不在账本列表里。</b> 它是给会验算的人的，摆在账单每一行上对
 * 其余所有人是噪音。放到详情最底部：想验的人一点即得，不想验的人永远看不到。
 */
export function RoundDetailSheet({
  round,
  currency,
  token,
  tier = '4',
  onClose,
}: {
  round: number;
  currency: string;
  token?: string | null;
  tier?: MilharTierId;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const countryCode = useCountryStore((state) => state.country.code);
  const [copied, setCopied] = useState<'idle' | 'ok' | 'manual'>('idle');
  const query = useQuery({
    // 国家进 key：金额按观看者的币种/国家下发（与 milhar-status 同一个理由，
    // 见 MilharPage.tsx 那条注释），游客切国家后 60s staleTime 内重开同一期，
    // 不许还在展示上一个国家缓存下来的金额配上新国家的币种符号。
    queryKey: ['milhar-round-detail', tier, round, countryCode],
    queryFn: () => fetchMilharRoundDetail(round, token, tier),
    staleTime: 60_000,
  });
  const d = query.data;
  // 点「本期都有谁」里的某一行 → 该玩家公开主页压在这张表上方（同 RoundBuyersSheet
  // 走 MilharPage.profileFor 的效果），关掉回到本期明细。渲染成 backdrop 的
  // 兄弟节点而不是子节点：两层同为 z-index 40，后者在 DOM 里靠后即压在上面，
  // 且点主页的遮罩不会冒泡到这张表的 onMouseDown 把它一起关掉。
  const [profileFor, setProfileFor] = useState<{ userId: number; username: string; avatarUrl?: string } | null>(null);

  return (
    <>
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="profile-sheet milhar-detail-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`#${round}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div className="flex-1">
            {/* 日内序号，和玩家在游戏页、注单历史、开奖列表看到的是同一个号。
                抬头曾经是全局期号（#40355）—— 点进来的人无法把它和刚才点的那一行
                对上。全局号仍在 title 与 aria 里，客服与链上核对拿得到。 */}
            <h2 className="mono" title={`global #${round}`}>{d?.label || `#${round}`}</h2>
            {/* 原来这里是「8 · 9」两个没有标签的裸数字，谁也看不懂那是什么 */}
            {d ? (
              <>
                <p className="milhar-detail-sub">
                  {/* 时间带日期 —— 只有 `22:55` 时，从「昨天」那一段点进来认不出
                      是哪天。缺值整段不渲染，不拿 0 冒充一个时刻（0 是 1970）。
                      用 shortDateTime 不用 localDateTime：后者是给 UTC/本地对照
                      栏用的，钉死 en-GB 还带秒；这里是账单标题，要跟着玩家的语言
                      走、不要秒（对齐统计页 HistoryTable 的日期形状）。 */}
                  {d.drawnAt > 0 ? `${shortDateTime(d.drawnAt, language)} · ` : ''}
                  {t('milharDetailCount')
                    .replace('%p%', String(d.totals.people))
                    .replace('%t%', String(d.totals.tickets))}
                </p>
                {/* 全局期号本来就是给客服工单和链上核对用的 —— 看得见复制不走
                    等于没给。剪贴板被浏览器拦时退化成「长按可选中」，不静默失败。 */}
                <button
                  type="button"
                  className={`milhar-detail-gid tap${copied === 'ok' ? ' is-copied' : ''}`}
                  aria-label={t('milharDetailCopyGlobalId')}
                  onClick={async () => {
                    const ok = await copyText(String(round));
                    setCopied(ok ? 'ok' : 'manual');
                    window.setTimeout(() => setCopied('idle'), 1800);
                  }}
                >
                  <span className="mono">
                    {t(copied === 'ok'
                      ? 'milharDetailGlobalIdCopied'
                      : copied === 'manual'
                        ? 'milharDetailGlobalIdSelect'
                        : 'milharDetailGlobalId').replace('%n%', String(round))}
                  </span>
                  <Copy size={12} aria-hidden="true" />
                </button>
                {/* 按钮自己的 aria-label 是静态的「复制全局期号」——「已复制」/「长按可选中」
                    这两个状态变化全靠按钮的可见文字传达，但静态 aria-label 会把那段文字
                    对读屏器整个盖掉（可访问名字优先取 aria-label，不会再去读子节点）。
                    这里另开一个不可见的 aria-live 节点，原样复述同一句话，读屏器用户才
                    能听到「已复制」——否则「绝不静默失败」这条要求，对读屏器用户就是
                    正正好好地静默失败了（本次评审 Finding 6）。 */}
                <span className="sr-only" aria-live="polite">
                  {copied === 'ok'
                    ? t('milharDetailGlobalIdCopied').replace('%n%', String(round))
                    : copied === 'manual'
                      ? t('milharDetailGlobalIdSelect').replace('%n%', String(round))
                      : ''}
                </span>
              </>
            ) : null}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="milhar-detail-body">
          {/* 三态分开：请求中给骨架，失败给一句话，成功才渲染。
              原来只有 `!d ? 骨架 : 内容` —— 请求失败时 d 永远是 undefined，
              用户会对着一个**永远空着的 sheet**，既没内容也没解释。 */}
          {query.isError ? (
            <p className="milhar-detail-error">{t('milharDetailUnavailable')}</p>
          ) : !d ? (
            <div className="milhar-detail-skeleton sk" />
          ) : (
            <>
              {/* 开奖号码。未开奖时不显示占位数字 —— 空串会被读成一个结果 */}
              {d.result ? (
                <div className="milhar-detail-draw mono" aria-label={d.result}>
                  {d.result.split('').map((c, i) => <span key={i}>{c}</span>)}
                </div>
              ) : null}

              <dl className="milhar-detail-rows">
                <div>
                  <dt>{t('milharDetailStaked')}</dt>
                  <dd className="mono">{fmtCurrency(Number(d.totals.staked), currency)}</dd>
                </div>
                <div>
                  <dt>{t('milharDetailPaid')}</dt>
                  <dd className="mono">{fmtCurrency(Number(d.totals.paid), currency)}</dd>
                </div>
                {d.ledger ? (() => {
                  // 比例现算，不读配置 —— 见 poolShare.ts 文件头
                  const share = poolSharePct(d.ledger.amount, d.totals.staked);
                  return (
                    <div>
                      <dt>
                        {t('milharDetailToPool')}
                        {share == null ? null : (
                          <small>{t('milharDetailToPoolShare')
                            .replace('%p%', formatPoolSharePct(share))}</small>
                        )}
                      </dt>
                      <dd className="mono">{fmtCurrency(Number(d.ledger.amount), currency)}</dd>
                    </div>
                  );
                })() : null}
              </dl>

              <h3 className="milhar-detail-subtitle">{t('milharDetailWhoPlayed')}</h3>
              <ul className="milhar-detail-bets">
                {d.bets.map((bet) => {
                  const won = Number(bet.payout) > 0;
                  // Why R$ 10.000 next to R$ 10: the digits matched, right to
                  // left. Without it two winning rows looked like a mistake.
                  const hits = won ? trailingMatch(bet.pick, d.result) : 0;
                  const url = bet.avatarThumbUrl
                    || (bet.avatarDefaultId ? defaultAvatarUrl(bet.avatarDefaultId) : '');
                  const canOpenProfile = bet.userId != null && bet.username.trim().length > 0;
                  const inner = (
                    <>
                      {url
                        ? <img className="milhar-feed-av" src={url} alt="" width={28} height={28} loading="lazy" />
                        : <span className="milhar-feed-av is-letter" style={{ width: 28, height: 28 }}>
                            {bet.username.trim().charAt(0).toUpperCase()}</span>}
                      <span className="milhar-detail-name">{bet.username}</span>
                      <span className="milhar-detail-pick mono">{bet.pick}</span>
                      {/* The digit tag sits under the amount, in the amount's
                          column — beside the pick it pushed the name column
                          around and nothing lined up. */}
                      <span className="milhar-detail-amt mono">
                        {won
                          ? `+${fmtCurrency(Number(bet.payout), currency)}`
                          : fmtCurrency(Number(bet.amount), currency)}
                        {hits > 0 ? <em className="milhar-detail-hit">{hits === 1 ? t('milharDetailHitOne') : t('hallNextHits').replace('%n%', String(hits))}</em> : null}
                      </span>
                    </>
                  );
                  const rowClass = `milhar-detail-bet${won ? ' is-won' : ''}`;
                  return (
                    <li key={bet.id}>
                      {canOpenProfile ? (
                        <button type="button" className={`${rowClass} milhar-detail-bet-btn tap`}
                          aria-label={t('raffleOpenProfile').replace('%username%', bet.username)}
                          onClick={() => setProfileFor({ userId: bet.userId, username: bet.username, avatarUrl: url || undefined })}>
                          {inner}
                        </button>
                      ) : (
                        <div className={rowClass}>{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* 后端一期最多下发 100 条（MilharRoundDetailService.BETS_LIMIT）。
                  票数更多时这张名单是残缺的，而上面的「Paid to winners」是按全量
                  算的 —— 两者一冲突，玩家看到的就是「本期赔付 R$ 10.215」配一张
                  合计只有 R$ 175 的名单（线上第 34272 期真实发生过）。所以截断
                  必须说出来。列表已按派彩降序，「先大后小」这半句同时告诉玩家：
                  没在这儿看到的大奖，不存在。 */}
              {d.bets.length < d.totals.tickets ? (
                <p className="milhar-detail-partial">
                  {t('milharDetailPartial')
                    .replace('%n%', String(d.bets.length))
                    .replace('%t%', String(d.totals.tickets))}
                </p>
              ) : null}

              {/* 验算层，压在最底 —— 想验的人找得到，不想验的人不会被它挡路 */}
              {d.ledger ? (
                <p className="milhar-detail-hash mono">{d.ledger.chainHash}</p>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
    {profileFor ? (
      <PublicProfileSheet user={profileSeed(profileFor)} onClose={() => setProfileFor(null)} />
    ) : null}
    </>
  );
}

// Count of digits matched from the right — Milhar's rule (compare right to left).
export function trailingMatch(pick: string | null | undefined, result: string | null | undefined): number {
  const a = String(pick || ''); const b = String(result || '');
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n += 1;
  return n;
}
